import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Mic, Users, Star, CheckCircle, Play, BookOpen, FileText, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { servicesData } from '../data/siteData';
import { useVideos, useBlogPosts, useBooks } from '../hooks/useContent';

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
};

export default function Speaking() {
  const service = servicesData.speaking;
  
  // Fetch content from Contentful
  const { videos = [], isLoading: videosLoading } = useVideos();
  const { blogPosts = [], isLoading: postsLoading } = useBlogPosts();
  const { books = [], isLoading: booksLoading } = useBooks();

  // Filter content by "Speaking" tag
  const relatedVideos = Array.isArray(videos) ? videos.filter(v => 
    v.tags?.some(tag => tag.toLowerCase().includes('speaking')) ||
    v.title?.toLowerCase().includes('speak')
  ).slice(0, 3) : [];

  const relatedPosts = Array.isArray(blogPosts) ? blogPosts.filter(p => 
    p.tags?.some(tag => tag.toLowerCase().includes('speaking')) ||
    p.title?.toLowerCase().includes('speak')
  ).slice(0, 3) : [];

  const relatedBooks = Array.isArray(books) ? books.filter(b => 
    b.tags?.some(tag => tag.toLowerCase().includes('speaking')) ||
    b.title?.toLowerCase().includes('speak')
  ).slice(0, 2) : [];

  const isLoading = videosLoading || postsLoading || booksLoading;
  const hasRelatedContent = relatedVideos.length > 0 || relatedPosts.length > 0 || relatedBooks.length > 0;

  return (
    <div data-testid="speaking-page">
      {/* Hero Section */}
      <section className="py-20 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 md:gap-20 items-center">
            {/* Content */}
            <motion.div
              initial="initial"
              animate="animate"
              variants={{ animate: { transition: { staggerChildren: 0.1 } } }}
            >
              <motion.p
                variants={fadeInUp}
                className="text-brand-secondary font-semibold tracking-widest uppercase text-sm mb-4"
              >
                {service.tagline}
              </motion.p>
              <motion.h1
                variants={fadeInUp}
                className="font-heading text-4xl md:text-6xl font-bold text-brand-primary leading-tight mb-6"
              >
                {service.title}
              </motion.h1>
              <motion.p variants={fadeInUp} className="text-slate-600 text-lg mb-8">
                {service.description}
              </motion.p>
              <motion.div variants={fadeInUp}>
                <Button
                  asChild
                  size="lg"
                  className="rounded-full bg-brand-secondary text-white hover:bg-brand-secondary/90 px-8 font-semibold shadow-lg shadow-orange-500/20"
                  data-testid="speaking-cta-btn"
                >
                  <Link to="/contact?service=speaking">
                    {service.cta} <ArrowRight className="ml-2 w-5 h-5" />
                  </Link>
                </Button>
              </motion.div>
            </motion.div>

            {/* Image */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-br from-brand-secondary/20 to-transparent rounded-3xl blur-2xl" />
                <img
                  src={service.image}
                  alt="Speaking"
                  className="relative w-full rounded-2xl shadow-xl"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Topics Section */}
      <section className="py-20 md:py-32 bg-brand-accent">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-brand-primary mb-6">
                Speaking Topics
              </h2>
              <p className="text-slate-600 mb-8">
                I deliver keynotes and presentations on a variety of topics, each designed to inspire
                action and create lasting change. Custom topics are also available based on your
                organization's specific needs.
              </p>
              <ul className="space-y-4">
                {service.topics.map((topic, index) => (
                  <motion.li
                    key={topic}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="flex items-start gap-3"
                  >
                    <CheckCircle className="w-6 h-6 text-brand-secondary flex-shrink-0 mt-0.5" />
                    <span className="text-brand-primary font-medium">{topic}</span>
                  </motion.li>
                ))}
              </ul>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 gap-6">
              <motion.div
                className="bg-white rounded-2xl p-6 text-center shadow-sm"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
              >
                <Mic className="w-10 h-10 text-brand-secondary mx-auto mb-4" />
                <div className="font-heading text-3xl font-bold text-brand-primary mb-1">500+</div>
                <div className="text-slate-600 text-sm">Keynotes Delivered</div>
              </motion.div>
              <motion.div
                className="bg-white rounded-2xl p-6 text-center shadow-sm"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
              >
                <Users className="w-10 h-10 text-brand-secondary mx-auto mb-4" />
                <div className="font-heading text-3xl font-bold text-brand-primary mb-1">100K+</div>
                <div className="text-slate-600 text-sm">Audience Members</div>
              </motion.div>
              <motion.div
                className="bg-white rounded-2xl p-6 text-center shadow-sm"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
              >
                <Star className="w-10 h-10 text-brand-secondary mx-auto mb-4" />
                <div className="font-heading text-3xl font-bold text-brand-primary mb-1">4.9/5</div>
                <div className="text-slate-600 text-sm">Average Rating</div>
              </motion.div>
              <motion.div
                className="bg-white rounded-2xl p-6 text-center shadow-sm"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3 }}
              >
                <div className="w-10 h-10 bg-brand-secondary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-brand-secondary font-bold text-lg">25</span>
                </div>
                <div className="font-heading text-3xl font-bold text-brand-primary mb-1">Countries</div>
                <div className="text-slate-600 text-sm">Worldwide</div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Related Content Section - From Contentful */}
      <section className="py-20 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-brand-primary mb-4">
              Related Content
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Explore videos, articles, and books tagged with "Speaking".
            </p>
          </motion.div>

          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-brand-secondary" />
            </div>
          ) : hasRelatedContent ? (
            <>
              {/* Related Videos */}
              {relatedVideos.length > 0 && (
                <div className="mb-12">
                  <h3 className="font-heading text-xl font-bold text-brand-primary mb-6 flex items-center gap-2">
                    <Play className="w-5 h-5 text-brand-secondary" />
                    Videos & Podcasts
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {relatedVideos.map((video, index) => (
                      <motion.div
                        key={video.id}
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: index * 0.1 }}
                      >
                        <Link to={`/videos/${video.slug}`} className="group block">
                          <div className="relative aspect-video rounded-xl overflow-hidden mb-3">
                            <img
                              src={video.thumbnail}
                              alt={video.title}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                            />
                            <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                              <Play className="w-10 h-10 text-white" />
                            </div>
                          </div>
                          <h4 className="font-semibold text-brand-primary group-hover:text-brand-secondary transition-colors line-clamp-2">
                            {video.title}
                          </h4>
                          <p className="text-sm text-slate-500 mt-1">{video.duration}</p>
                        </Link>
                      </motion.div>
                    ))}
                  </div>
                </div>
              )}

              {/* Related Blog Posts */}
              {relatedPosts.length > 0 && (
                <div className="mb-12">
                  <h3 className="font-heading text-xl font-bold text-brand-primary mb-6 flex items-center gap-2">
                    <FileText className="w-5 h-5 text-brand-secondary" />
                    Articles
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {relatedPosts.map((post, index) => (
                      <motion.div
                        key={post.id}
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: index * 0.1 }}
                      >
                        <Link to={`/blog/${post.slug}`} className="group block">
                          <div className="aspect-[4/3] rounded-xl overflow-hidden mb-3">
                            <img
                              src={post.image}
                              alt={post.title}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                            />
                          </div>
                          <h4 className="font-semibold text-brand-primary group-hover:text-brand-secondary transition-colors line-clamp-2">
                            {post.title}
                          </h4>
                          <p className="text-sm text-slate-500 mt-1">{post.readTime}</p>
                        </Link>
                      </motion.div>
                    ))}
                  </div>
                </div>
              )}

              {/* Related Books */}
              {relatedBooks.length > 0 && (
                <div>
                  <h3 className="font-heading text-xl font-bold text-brand-primary mb-6 flex items-center gap-2">
                    <BookOpen className="w-5 h-5 text-brand-secondary" />
                    Books
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {relatedBooks.map((book, index) => (
                      <motion.div
                        key={book.id}
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: index * 0.1 }}
                      >
                        <Link to={`/books/${book.slug}`} className="group flex gap-4 p-4 bg-brand-accent rounded-xl hover:shadow-lg transition-shadow">
                          <img
                            src={book.coverImage}
                            alt={book.title}
                            className="w-24 h-32 object-cover rounded-lg shadow"
                          />
                          <div className="flex-1">
                            <h4 className="font-semibold text-brand-primary group-hover:text-brand-secondary transition-colors">
                              {book.title}
                            </h4>
                            {book.subtitle && (
                              <p className="text-sm text-slate-600 mt-1">{book.subtitle}</p>
                            )}
                            <p className="text-sm text-slate-500 mt-2 line-clamp-2">{book.description}</p>
                          </div>
                        </Link>
                      </motion.div>
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-12 bg-brand-accent rounded-2xl">
              <p className="text-slate-600 mb-2">No related content yet.</p>
              <p className="text-sm text-slate-500">
                Tag your content with "Speaking" in Contentful to show it here.
              </p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
